import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { useMissionDebugContext } from '../context/MissionDebugContext';
import {
  MissionDebugStateOptions,
  MissionDebugStateItem,
  MissionDebugItemType,
} from '../types/missionDebug';

/**
 * Custom hook that wraps useState and registers the state for debugging
 * via the MissionDebugContext.
 *
 * @template T The type of the state variable.
 * @param initialValue The initial value for the state.
 * @param key A unique key identifying this state within the component instance.
 * @param options Configuration options (label, type hint, min/max/step) for the debug panel.
 * @returns A stateful value and a function to update it (same as useState).
 */
export function useMissionDebugState<T>(
  initialValue: T | (() => T),
  key: string,
  options: MissionDebugStateOptions
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(initialValue);
  const { registerItem } = useMissionDebugContext();

  // Memoize the getter function to ensure stability
  // This function will be captured by the registration effect
  const getValue = useCallback(() => value, [value]);

  // Effect to register the state with the provider
  // It runs when the registration details (key, options, callbacks) change.
  // Crucially, it does *not* run just because `value` changes.
  // The `getValue` callback ensures the Debug Panel always gets the latest value.
  useEffect(() => {
    // Determine the type hint for the debug panel
    let determinedType: MissionDebugItemType = 'object'; // Default
    if (options.type) {
      determinedType = options.type;
    } else if (typeof value === 'string') {
      determinedType = 'string';
    } else if (typeof value === 'number') {
      determinedType = 'number';
    } else if (typeof value === 'boolean') {
      determinedType = 'boolean';
    }

    const stateItem: MissionDebugStateItem<T> = {
      kind: 'state',
      label: options.label,
      type: determinedType,
      getValue,
      // Cast needed because SetStateAction<T> could be a function `(prevState: T) => T`
      // We only want the direct `(value: T) => void` form for the debug panel.
      // This assumes the debug panel will always set a direct value, not use the function form.
      setValue: setValue as (val: T) => void,
      options: {
        min: options.min,
        max: options.max,
        step: options.step,
      },
    };
    registerItem(key, stateItem);

    // No cleanup needed here; the provider handles unregistering the whole map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerItem, key, options.label, options.type, options.min, options.max, options.step, getValue, setValue]);

  return [value, setValue];
} 